
import SelectInstitute from '../../components/admin/SelectInstitute'
import { Typography, Box, useTheme, Skeleton } from '@mui/material'
import { useCallback, useEffect, useMemo } from 'react'
import { observer } from 'mobx-react'
import React from 'react'

import CategoricalBarGraph from '../../components/common/CategoricalBarGraph'
import { useOrganizationStore } from '../../store/organization.store'
import MultiBarGraph from '../../components/common/MultiBarGraph'
import CountCard from '../../components/common/CountCard'
import { useUserStore } from '../../store/user.store'
import SubadminDashboard from './SubadminDashboard'
import { Icons } from '../../utils/utilities'
import api from '../../service/api'

const graphColors = [
    "#5470c6",
    "#91cc75",
    "#fac858",
    "#ee6666",
    "#73c0de",
    "#3ba272",
    "#fc8452",
    "#9a60b4"
]

const graphsList = [
    { id: "assignments", plots: [{ datakey: "assignments_created", label: "Assignments", color: graphColors[0] }], graphTitle: "Assignments created in last 12 months", requestFuction: api.audit.getAssignmentStats },
    { id: "quizzes", plots: [{ datakey: "quizzes_created", label: "Quizzes", color: graphColors[1] }], graphTitle: "Quizzes created in last 12 months", requestFuction: api.audit.getQuizzesStats },
    { id: "studymaterials", plots: [{ datakey: "resources_added", label: "Study material attachments", color: graphColors[2] }], graphTitle: "Materials uploaded in last 12 months", requestFuction: api.audit.fetchStudyMaterialStats },
    { id: "exams", plots: [{ datakey: "exams", label: "Exams", color: graphColors[3] }], graphTitle: "Exams conducted in last 12 months", requestFuction: api.audit.getExamsStats },
]

const Analytics = observer( () => {
    const [graphsData, setGraphsData] = React.useState( [] )
    const [instituteCode, setInstituteCode] = React.useState( "all" )
    const [hasPermission, setHasPermission] = React.useState( true )
    const [loadingCounts, setLoadingCounts] = React.useState( true )
    const [loadingGraphs, setLoadingGraphs] = React.useState( true )
    const [allStats, setAllStats] = React.useState( {} )

    const OrganizationStore = useOrganizationStore()
    const UserStore = useUserStore()

    const { palette, layout, border } = useTheme()

    const handleChange = async ( val ) => {
        setLoadingCounts( true )
        setInstituteCode( val )
        // loadAllStat( val )
        setLoadingCounts( false )
    }

    const getFacultyCount = async () => {
        try {
            const res = await api.getInstituteFacultyCount()
            return res?.data?.data || {}
        } catch ( err ) {
            console.log( err )
            return {}
        }
    }

    const getStudentCount = async () => {
        try {
            const res = await api.getInstituteStudentCount()
            return res?.data?.data || {}
        } catch ( err ) {
            console.log( err )
            return {}
        }
    }

    const getBranchCount = async () => {
        try {
            const res = await api.getInstituteBranchCount()
            return res?.data?.data || {}
        } catch ( err ) {
            console.log( err )
            return 0
        }
    }

    const getStudyMaterialsCount = async () => {
        try {
            const res = await api.getInstituteStudyMaterialCount()
            return res?.data?.data?.count ?? 0
        } catch ( err ) {
            console.log( err )
            return 0
        }
    }

    const getAssignmentCount = async () => {
        try {
            const res = await api.getInstituteAssignmentCount()
            return res?.data?.data || {}
        } catch ( err ) {
            console.log( err )
            return 0
        }
    }

    const getPracticeSessionsCount = async () => {
        try {
            const res = await api.getPracticeSessionsCount()
            return res?.data?.data || {}
        } catch ( err ) {
            console.log( err )
            return 0
        }
    }

    const getExamCount = async () => {
        try {
            const res = await api.getInstituteExamCount()
            return res?.data?.data ?? {}
        } catch ( err ) {
            console.log( err )
            return 0
        }
    }

    const getGraphData = useCallback( async () => {
        try {

            const requests = graphsList.map( g => g.requestFuction() )

            const responses = await Promise.all( requests )

            const data = []
            responses.forEach( ( response, index ) => {
                if ( response?.data?.data?.last12MonthsStats ) {
                    const graphDetails = { ...graphsList[index] }
                    graphDetails.data = response?.data?.data?.last12MonthsStats
                    data.push( graphDetails )
                }
            } )

            setGraphsData( data )

        } catch ( err ) {

        } finally {
            setLoadingGraphs( false )
        }
    }, [] )

    const getAllData = useCallback( async () => {
        setLoadingCounts( true )
        const allStats = {}



        const [
            faculty_count,
            student_count,
            branch_count,
            assignments_count,
            practice_sessions_count,
            exams
        ] = await Promise.all( [
            getFacultyCount(),
            getStudentCount(),
            getBranchCount(),
            getAssignmentCount(),
            getPracticeSessionsCount(),
            getExamCount(),
        ] )

        allStats.faculty_count = faculty_count
        allStats.student_count = student_count
        allStats.branch_count = branch_count
        allStats.assignments_count = assignments_count
        allStats.practice_sessions_count = practice_sessions_count
        allStats.exams_count = exams

        if ( OrganizationStore.getOrganization?.length === 0 ) {
            await OrganizationStore.fetchOrganization()
        }
        setAllStats( allStats )

        setLoadingCounts( false )
    }, [OrganizationStore] )

    useEffect( () => {

        if ( UserStore.getUser.user_role === 'SUBADMIN' && !UserStore.getUser.user_permissions.analytics ) {
            setHasPermission( false )
        }
        else {
            getAllData()
            getGraphData()
        }
    }, [UserStore, getAllData, getGraphData] )

    const selectedInstituteStats = React.useMemo( () => {
        if ( !allStats || Object.keys( allStats ).length === 0 ) return {}

        if ( instituteCode === "all" ) {
            const total = {
                study_materials_count: 0,
                exams_count: 0,
                users_count: []
            }

            Object.values( allStats ).forEach( stat => {
                total.study_materials_count += +( stat?.study_materials_count ?? 0 )
                total.exams_count += +( stat?.exams_count ?? 0 )
                // total.users_count = []
            } )

            return total
        }

        return allStats[instituteCode] ?? {}
    }, [allStats, instituteCode] )

    const monthWiseGraphData = useMemo( () => {
        const monthWiseData = []
        for ( const resourceGraphDetails of graphsData ) {
            const { id, plots, graphTitle, data = {} } = resourceGraphDetails
            const filteredData = { id, plots, graphTitle }

            if ( instituteCode === "all" ) {
                const formattedData = {}
                const institutes = Object.keys( data )
                for ( const institute of institutes ) {
                    for ( const month in data[institute] ) {
                        if ( !Object.hasOwn( data[institute], month ) ) continue

                        const dataMap = new Map( Object.entries( data[institute][month] || {} ) )
                        if ( !formattedData[month] ) {
                            formattedData[month] = { month }
                            for ( const plot of plots ) {
                                formattedData[month][plot.datakey] = 0
                            }
                        }
                        for ( const plot of plots ) {
                            formattedData[month][plot.datakey] += dataMap.get( plot.datakey )
                        }

                    }
                }
                filteredData.graphData = Object.values( formattedData )

            } else {
                const formattedData = {}

                for ( const month in data[instituteCode] ) {
                    if ( !Object.hasOwn( data[instituteCode], month ) ) continue

                    const dataMap = new Map( Object.entries( data[instituteCode][month] || {} ) )
                    if ( !formattedData[month] ) {
                        formattedData[month] = { month }
                        for ( const plot of plots ) {
                            formattedData[month][plot.datakey] = 0
                        }
                    }
                    for ( const plot of plots ) {
                        formattedData[month][plot.datakey] += dataMap.get( plot.datakey )
                    }

                }

                filteredData.graphData = Object.values( formattedData )
            }
            monthWiseData.push( filteredData )
        }

        return monthWiseData
    }, [graphsData, instituteCode] )


    return (
        hasPermission ? <Box padding={layout.pagePadding} overflow="auto" flexGrow={1}>
            <Box display="flex" flexDirection="column" gap={layout.sectionsGap} >
                <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} alignItems={{ sm: "center", xs: "stretch" }} gap="20px" flexWrap="wrap" justifyContent="space-between" >
                    <Box flexGrow={1}>
                        <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Admin Dashboard</Typography>
                        <Typography variant="subtitle2" fontSize="12px" color='textSecondary'>A comprehensive breakdown of system activity and resource distribution.</Typography>
                    </Box>
                    <SelectInstitute showDefault={false} showAllOption value={instituteCode} onSelect={handleChange} />
                </Box>
                <Box
                    display="grid"
                    gridTemplateColumns={{
                        md: "repeat(5,1fr)",
                        sm: "repeat(4,1fr)",
                        xs: "repeat(2,1fr)"
                    }}
                    gap="10px"
                    flexWrap="wrap"
                    justifyContent="stretch"
                >
                    <CountCard loading={loadingCounts} value={allStats?.faculty_count?.[instituteCode]?.total || 0} label="Faculty Count" icon={Icons.default.People} />
                    <CountCard loading={loadingCounts} value={allStats?.faculty_count?.[instituteCode]?.hod || 0} label="HODs" icon={Icons.default.SupervisedUserCircle} />
                    <CountCard loading={loadingCounts} value={allStats?.student_count?.[instituteCode] || 0} label="Students" icon={Icons.default.Group} />
                    <CountCard loading={loadingCounts} value={allStats.branch_count?.[instituteCode] || 0} label="Branches" icon={Icons.default.Branches} />
                    <CountCard loading={loadingCounts} value={allStats.practice_sessions_count?.[instituteCode] || 0} label="Practice Sessions" icon={Icons.default.Checklist} />
                    <CountCard loading={loadingCounts} value={allStats.assignments_count?.[instituteCode] || 0} label="Assignments" icon={Icons.default.AssignmentIcon} />
                    <CountCard loading={loadingCounts} value={allStats.exams_count?.[instituteCode] || 0} label="Exams" icon={Icons.default.QuizIcon} />
                </Box>

                {!loadingCounts && <Box
                    display="grid"
                    gridTemplateColumns={{
                        md: "repeat(2,1fr)",
                        sm: "repeat(1,1fr)",
                        xs: "repeat(1,1fr)"
                    }}
                    gap="10px"
                    flexWrap="wrap"
                    color={palette.text.primary}
                    justifyContent="stretch"
                >
                    {monthWiseGraphData.map( ( data, index ) => {

                        const series = data.plots.map( plot => {

                            const { datakey, label, color = graphColors[index % graphColors.length] } = plot

                            return {
                                key: datakey,
                                label,
                                color
                            }
                        } )

                        return (
                            <MultiBarGraph key={data.id} xAxisLabel="Month" xAxisKey='month' seriesConfig={series} title={data.graphTitle} data={data.graphData} />
                        )
                    } )}
                </Box>}

                {loadingCounts && <Box
                    display="grid"
                    gridTemplateColumns={{
                        md: "repeat(2,1fr)",
                        sm: "repeat(1,1fr)",
                        xs: "repeat(1,1fr)"
                    }}
                    gap="10px"
                    flexWrap="wrap"
                >
                    <Box display="flex" bgcolor={palette.contentBg} padding={layout.pagePadding} borderRadius={layout.borderRadius} flexDirection="column" gap="10px">
                        <Box display="flex" justifyContent="space-between">
                            <Skeleton sx={{ width: "40%", borderRadius: "5px" }} variant='h6'></Skeleton>
                            <Skeleton sx={{ width: "20%", borderRadius: "5px" }} variant='subtitle2'></Skeleton>
                        </Box>
                        <Skeleton variant='rectangular' width="100%" sx={{ width: "100%", borderRadius: layout.borderRadius }} height={250} > </Skeleton>
                    </Box>
                    <Box display="flex" bgcolor={palette.contentBg} padding={layout.pagePadding} borderRadius={layout.borderRadius} flexDirection="column" gap="10px">
                        <Box display="flex" justifyContent="space-between">
                            <Skeleton sx={{ width: "40%", borderRadius: "5px" }} variant='h6'></Skeleton>
                            <Skeleton sx={{ width: "20%", borderRadius: "5px" }} variant='subtitle2'></Skeleton>
                        </Box>
                        <Skeleton variant='rectangular' width="100%" sx={{ width: "100%", borderRadius: layout.borderRadius }} height={250} > </Skeleton>
                    </Box>
                </Box>}

                {!loadingGraphs && instituteCode !== "all" && selectedInstituteStats.users_count?.length > 0 && <Box height="400px">
                    <CategoricalBarGraph
                        hideLegend
                        barData={selectedInstituteStats.users_count}
                        xAxisValuesKey="branch"
                        barValuesKey="count"
                        graphTitle={`User(s) across ${instituteCode} branches`}
                        xAxisName="Branch"
                        yAxisName="Users count"
                        showLabels
                    />
                </Box>}

            </Box>
        </Box> :
            <SubadminDashboard />
    )
} )

export default Analytics
